import { z } from 'zod';
import {
  NotificationChannelSchema,
  SubscriptionPlan,
  SubscriptionPlanSchema,
  SubscriptionStatusSchema,
} from './domain/common';
export const PlanLimitsSchema = z.object({
  daily_words: z.number(),
  max_topics: z.number(),
  pronunciation: z.boolean(),
  multi_language_limit: z.number(),
  custom_delivery_times: z.number(),
  slang: z.boolean(),
  funny_facts: z.boolean(),
  related_words: z.boolean(),
  delivery_channels: z.array(NotificationChannelSchema),
});
const BaseSubscriptionSchema = z.object({
  plan: SubscriptionPlanSchema,
  status: SubscriptionStatusSchema,
  subscriptionExpiresAt: z.string().optional(),
  limits: PlanLimitsSchema.optional(),
  url: z.string().optional(),
  updated: z.boolean().optional(),
  canceled: z.boolean().optional(),
  message: z.string().optional(),
});
export const SubscriptionSchemas = {
  userDetails: BaseSubscriptionSchema.extend({
    plan: SubscriptionPlanSchema,
    status: SubscriptionStatusSchema,
    subscriptionExpiresAt: z.string().nullable(),
    limits: PlanLimitsSchema,
  }),
  operationSuccess: z.object({
    canceled: z.boolean().optional(),
    updated: z.boolean().optional(),
    message: z.string(),
  }),
  checkoutResult: z.object({
    url: z.string(),
  }),
  planChangeRequest: z.object({
    plan: SubscriptionPlanSchema,
  }),
  upgradeRequest: z.object({
    plan: SubscriptionPlanSchema.refine(
      plan => plan === SubscriptionPlan.Pro || plan === SubscriptionPlan.Ultra,
      { message: 'Upgrade plan must be pro or ultra' }
    ),
  }),
  downgradeRequest: z.object({
    plan: SubscriptionPlanSchema.refine(
      plan => plan === SubscriptionPlan.Free || plan === SubscriptionPlan.Pro,
      { message: 'Downgrade plan must be free or pro' }
    ),
  }),
  base: BaseSubscriptionSchema,
};
export type PlanLimits = z.infer<typeof PlanLimitsSchema>;
export type UserSubscriptionDetails = z.infer<
  typeof SubscriptionSchemas.userDetails
>;
export type SubscriptionOperationSuccess = z.infer<
  typeof SubscriptionSchemas.operationSuccess
>;
export type CheckoutResult = z.infer<typeof SubscriptionSchemas.checkoutResult>;
export type BaseSubscription = z.infer<typeof SubscriptionSchemas.base>;
const EmptyRequestSchema = z.object({});
export const SubscriptionsAPI = {
  upgrade: {
    request: SubscriptionSchemas.upgradeRequest,
    response: SubscriptionSchemas.checkoutResult.or(
      SubscriptionSchemas.operationSuccess
    ),
  },
  downgrade: {
    request: SubscriptionSchemas.downgradeRequest,
    response: SubscriptionSchemas.operationSuccess,
  },
  getUserSubscription: {
    request: EmptyRequestSchema,
    response: SubscriptionSchemas.userDetails,
  },
  cancel: {
    request: EmptyRequestSchema,
    response: SubscriptionSchemas.operationSuccess,
  },
};
export type UpgradeSubscriptionRequest = z.infer<
  typeof SubscriptionsAPI.upgrade.request
>;
export type UpgradeSubscriptionResponse = z.infer<
  typeof SubscriptionsAPI.upgrade.response
>;
export type DowngradeSubscriptionRequest = z.infer<
  typeof SubscriptionsAPI.downgrade.request
>;
export type DowngradeSubscriptionResponse = z.infer<
  typeof SubscriptionsAPI.downgrade.response
>;
export type GetUserSubscriptionRequest = z.infer<
  typeof SubscriptionsAPI.getUserSubscription.request
>;
export type GetUserSubscriptionResponse = z.infer<
  typeof SubscriptionsAPI.getUserSubscription.response
>;
export type CancelSubscriptionRequest = z.infer<
  typeof SubscriptionsAPI.cancel.request
>;
export type CancelSubscriptionResponse = z.infer<
  typeof SubscriptionsAPI.cancel.response
>;
export type SubscriptionChangeResponse =
  | UpgradeSubscriptionResponse
  | DowngradeSubscriptionResponse;
export const UpgradeSubscriptionRequestSchema =
  SubscriptionSchemas.upgradeRequest;
export const DowngradeSubscriptionRequestSchema =
  SubscriptionSchemas.downgradeRequest;
